import { forwardRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

/** shadcn/ui 風 Select ラッパー (v0.3 業態初期表示自動選択、Design C-5)。
 *  タップ領域44px確保・キーボード操作の一貫性のため native <select> から移行。
 *  z-index は docs/ui-guidelines.md §6 の popover 全般 (z-50) に合わせる。 */
export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;
export const SelectGroup = SelectPrimitive.Group;

/** グループ見出し (使い勝手フィードバック増分⑦ D-U37、申請書一覧のカテゴリ見出しで初採用)。
 *  選択不可の非対話要素のため SelectItem のタップ領域規約 (44px) の対象外。 */
export const SelectLabel = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 pt-2 pb-1 text-xs font-semibold text-neutral-sub', className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex w-full min-h-[44px] items-center justify-between gap-2 rounded border border-neutral-line bg-white px-3 py-2',
      'text-sm text-left focus-ring disabled:opacity-50 disabled:cursor-not-allowed',
      className,
    )}
    {...props}
  >
    {/* Design レビュー指摘: 選択済みの値が長いラベル (シフト期間名等) だとトリガー幅を超えて
        はみ出す。@radix-ui/react-select の SelectValue は className/style を破棄する実装のため
        (SelectItem の ItemText と同型)、呼び出し元で <SelectValue/> に直接 truncate を渡せない。
        ここで children (= SelectValue) を min-w-0 + truncate の span で包む */}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDown size={14} className="shrink-0 text-neutral-sub" aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

/** 選択肢がビューポートに収まらないときに出る上下スクロールボタン。
 *  ⚠️ これが無いと Radix は自動スクロールを提供しない。実際に「期間プリセット (36ヶ月) を開くと
 *  画面外へはみ出してスクロールできず、目的の月を選べない」という不具合が本番で発生した
 *  (2026-08-17)。長い選択肢を持つのは月選択だけでなく、食材マスタ・659店舗規模の店舗選択も
 *  同様のため、個別画面ではなく共有コンポーネント側で解消する。 */
const scrollButtonClass = 'flex cursor-default items-center justify-center py-1 text-neutral-sub bg-white';

export const SelectScrollUpButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton ref={ref} className={cn(scrollButtonClass, className)} {...props}>
    <ChevronUp size={14} aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = 'SelectScrollUpButton';

export const SelectScrollDownButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton ref={ref} className={cn(scrollButtonClass, className)} {...props}>
    <ChevronDown size={14} aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = 'SelectScrollDownButton';

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        'z-50 overflow-hidden rounded border border-neutral-line bg-white shadow-lg',
        // ⚠️ 高さ上限が無いと、選択肢が多いときにポップオーバーが画面外へ伸びて到達できなくなる。
        //    --radix-select-content-available-height は Radix が「トリガーから画面端までの実際の余白」を
        //    入れてくれる変数。上下どちらに開いても必ず画面内に収まる。
        'max-h-[var(--radix-select-content-available-height)]',
        // Design レビュー指摘: 選択肢テキストの長さに上限が無いと (例: シフト期間名 最大200文字)
        // ポップオーバーが画面外へ横に伸びうる。幅にも上限を設け、はみ出す分は SelectItem 側で truncate する
        'max-w-[min(90vw,24rem)]',
        position === 'popper' && 'min-w-[var(--radix-select-trigger-width)]',
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      {/* Viewport 側にも上限とスクロールを持たせる (Content だけだと中身が溢れたまま切られる) */}
      <SelectPrimitive.Viewport className="p-1 max-h-[var(--radix-select-content-available-height)] overflow-y-auto">
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex min-h-[44px] sm:min-h-0 min-w-0 cursor-pointer select-none items-center rounded py-2 pl-7 pr-2 text-sm',
      'outline-none focus:bg-neutral-panel data-[state=checked]:font-medium',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check size={14} className="text-brand-red" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
    {/* Design レビュー指摘: 長いラベル (シフト期間名・組織ノード名等) がポップオーバーを画面外へ
        押し広げないよう、ここで truncate する (SelectContent の max-w と対)。
        ⚠️ @radix-ui/react-select の ItemText は className/style を props から明示的に取り除いて
        破棄する実装のため (dist/index.js の SelectItemText2 参照)、直接 className を渡しても
        効かない。内側に truncate 用の span を1枚挟むこと */}
    <SelectPrimitive.ItemText>
      <span className="block min-w-0 truncate">{children}</span>
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';
